import { FormEvent, useCallback, useEffect, useState } from 'react';
import { AiOutlineCheck } from 'react-icons/ai';
import { BsExclamation } from 'react-icons/bs';
import { FiChevronLeft, FiChevronRight, FiEye, FiEyeOff } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

import logo from '../../assets/images/logos/logo-white.png';
import { Loading } from '../../components/Loading';
import { StatusModal } from '../../components/StatusModal';
import { useAuth } from '../../hooks/auth';
import { emailValidation } from '../../utils/EmailValidation';
import styles from './styles.module.scss';

export const Home = () => {
  const { user, signIn, forgotPassword, changePassword } = useAuth();
  const navigate = useNavigate();

  const [emailOK, setEmailOK] = useState<'notOk' | 'vazio' | 'ok'>('vazio');
  const [emailError, setEmailError] = useState('');
  const [email, setEmail] = useState('');

  const [passwordOK, setPasswordOK] = useState<'notOk' | 'vazio' | 'ok'>(
    'vazio'
  );
  const [passwordError, setPasswordError] = useState('');
  const [password, setPassword] = useState('');

  const [newPasswordOK, setNewPasswordOK] = useState<'notOk' | 'vazio' | 'ok'>(
    'vazio'
  );
  const [newPasswordError, setNewPasswordError] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const [confirmNewPasswordOK, setConfirmNewPasswordOK] = useState<
    'notOk' | 'vazio' | 'ok'
  >('vazio');
  const [confirmNewPasswordError, setConfirmNewPasswordError] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');

  const [confirmationCodeOK, setConfirmationCodeOK] = useState<
    'notOk' | 'vazio' | 'ok'
  >('vazio');
  const [confirmationCodeError, setConfirmationCodeError] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');

  const [visible, setVisible] = useState(true);

  const [reminder, setReminder] = useState(false);

  const [loading, setLoading] = useState(false);
  const [formState, setFormState] = useState<'SignIn' | 'Confirm' | 'Forgot'>(
    'SignIn'
  );

  const [modal, setModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalType, setModalType] = useState<'success' | 'error'>('success');

  const handleInputEmail = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      const { value } = e.currentTarget;
      const emailValid = emailValidation({
        email: value,
        emptyEmail: 'Preenchimento obrigatório',
        incorrectEmail: 'Preenchimento incorreto',
      });
      setEmailOK(emailValid.status);
      setEmailError(emailValid.message);
      setEmail(value);
    },
    []
  );

  const handleInputPassword = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      const { value } = e.currentTarget;
      const regex = /^(?=.*?[a-z])(?=.*?[0-9]).{6,}$/;
      if (value.length === 0) {
        setPasswordError('Preenchimento obrigatório');
        setPasswordOK('notOk');
      } else if (!value.match(regex)) {
        setPasswordError('Preenchimento incorreto');
        setPasswordOK('notOk');
      } else {
        setPasswordError('');
        setPasswordOK('ok');
      }

      setPassword(value);
    },
    []
  );

  const handleInputNewPassword = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      const { value } = e.currentTarget;
      const regex = /^(?=.*?[a-z])(?=.*?[0-9]).{6,}$/;
      if (value.length === 0) {
        setNewPasswordError('Preenchimento obrigatório');
        setNewPasswordOK('notOk');
      } else if (!value.match(regex)) {
        setNewPasswordError('Preenchimento incorreto');
        setNewPasswordOK('notOk');
      } else {
        setNewPasswordError('');
        setNewPasswordOK('ok');
      }

      setNewPassword(value);
    },
    []
  );

  const handleInputConfirmNewPassword = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      const { value } = e.currentTarget;
      if (value !== newPassword) {
        setConfirmNewPasswordError('Preenchimento incorreto');
        setConfirmNewPasswordOK('notOk');
      } else if (value.length === 0) {
        setConfirmNewPasswordError('Preenchimento obrigatório');
        setConfirmNewPasswordOK('notOk');
      } else {
        setConfirmNewPasswordError('');
        setConfirmNewPasswordOK('ok');
      }
      setConfirmNewPassword(value);
    },
    [newPassword]
  );

  const handleInputCode = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      e.currentTarget.maxLength = 6;
      let { value } = e.currentTarget;
      value = value.replace(/\D/g, '');
      setConfirmationCode(value);
      // eslint-disable-next-line no-unused-expressions
      value.length < 6
        ? setConfirmationCodeOK('notOk')
        : setConfirmationCodeOK('ok');
      if (value.length === 0) {
        setConfirmationCodeError('Preenchimento obrigatório');
      } else if (value.length < 6) {
        setConfirmationCodeError('Preenchimento incorreto');
      } else {
        setConfirmationCodeError('');
      }
    },
    []
  );

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      try {
        e.preventDefault();
        setLoading(true);

        const result = await signIn({ email, password });

        console.log(result);

        if (result.status === 'success') {
          navigate('/admin');
        } else {
          const errorMessage =
            String(result.message).search('Incorrect username or password.') ||
            String(result.message).search('User not found') > -1
              ? 'Email / Senha incorreto'
              : 'Error ao realizar login, tente novamente';
          setModal(true);
          setModalType('error');
          setModalMessage(errorMessage);
        }
      } catch (error: any) {
        setModal(true);
        setModalType('error');
        setModalMessage('Error ao realizar login, tente novamente');
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [email, navigate, password, signIn]
  );

  const handleForgotPassword = useCallback(async () => {
    try {
      if (emailOK !== 'ok') {
        setEmailError('Preenchimento obrigatório');
        setEmailOK('notOk');
        return;
      }

      setLoading(true);
      const result = await forgotPassword(email);

      if (result.status === 'success') {
        if (result.data.message.status === 'Senha enviada por e-mail') {
          setModalMessage(result.data.message.status);
          setModalType('success');
          setModal(true);
        } else {
          setFormState('Forgot');
          setModalMessage('Código enviado por e-mail');
          setModalType('success');
          setModal(true);
        }
      } else {
        const errorMessage =
          String(result.message).search('Incorrect username or password.') > -1
            ? 'Email / Senha incorreto'
            : String(result.message).search('User not found') > -1
            ? 'Email não encontrado'
            : 'Erro ao enviar código de recuperação de senha, tente novamente!';
        setModal(true);
        setModalType('error');
        setModalMessage(errorMessage);
      }
    } catch (error) {
      setModal(true);
      setModalType('error');
      setModalMessage(
        'Erro ao solicitar processo de recuperação de senha, tente novamente'
      );
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [email, emailOK, forgotPassword]);

  const handleChangePassword = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      try {
        if (emailOK !== 'ok') {
          setEmailError('Preenchimento obrigatório');
          setEmailOK('notOk');
          return;
        }

        setLoading(true);
        const result = await changePassword(
          email,
          newPassword,
          confirmationCode
        );

        if (result.status === 'success') {
          setFormState('SignIn');
        } else {
          const errorMessage =
            String(result.message).search(
              'Invalid verification code provided'
            ) > -1
              ? 'Código incorreto!'
              : 'Error ao realizar troca de senha, tente novamente';
          setModal(true);
          setModalType('error');
          setModalMessage(errorMessage);
        }
      } catch (error) {
        setModal(true);
        setModalType('error');
        setModalMessage(
          'Erro ao solicitar processo de recuperação de senha, tente novamente'
        );
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [emailOK, changePassword, email, newPassword, confirmationCode]
  );

  useEffect(() => {
    if (user.id) {
      navigate('/admin/dashboard');
    }
  }, [navigate, user]);

  return (
    <>
      {loading && <Loading />}
      {modal && (
        <StatusModal setModal={setModal} text={modalMessage} type={modalType} />
      )}
      <div className={styles.container}>
        <div className={styles.imageContainer}>
          <div className={styles.zigLogo}>
            <img src={logo} alt="" />
          </div>
        </div>
        <div className={styles.formContainer}>
          {formState === 'SignIn' && (
            <form className={styles.form} onSubmit={handleSubmit}>
              <span className={styles.formTitle}>Faça o seu login</span>
              <div className={styles.formContent}>
                <div className={styles.inputsContainer}>
                  <div className={styles.inputContainer}>
                    <div className={styles.input} role={emailOK}>
                      <label htmlFor="email">Email</label>
                      <input
                        type="text"
                        name="email"
                        placeholder="Email"
                        onChange={handleInputEmail}
                        value={email}
                      />
                      <AiOutlineCheck className={styles.check} role={emailOK} />
                      <BsExclamation className={styles.danger} role={emailOK} />
                    </div>
                    <p className={styles.error}>{emailError}</p>
                  </div>
                  <div className={styles.inputContainer}>
                    <div className={styles.input} role={passwordOK}>
                      <label htmlFor="password">Senha</label>
                      <input
                        type={visible ? 'password' : 'text'}
                        name="password"
                        placeholder="Senha"
                        value={password}
                        onChange={handleInputPassword}
                      />
                      {visible ? (
                        <FiEyeOff
                          onClick={() => {
                            setVisible(!visible);
                          }}
                          style={{ cursor: 'pointer', marginRight: '8px' }}
                        />
                      ) : (
                        <FiEye
                          onClick={() => {
                            setVisible(!visible);
                          }}
                          style={{ cursor: 'pointer', marginRight: '8px' }}
                        />
                      )}
                      <AiOutlineCheck
                        className={styles.check}
                        role={passwordOK}
                      />
                      <BsExclamation
                        className={styles.danger}
                        role={passwordOK}
                      />
                    </div>
                    <p className={styles.error}>{passwordError}</p>
                  </div>
                </div>
                <div className={styles.formReminder}>
                  <div
                    className={styles.reminder}
                    role={reminder ? 'check' : ''}
                  >
                    <div onClick={() => setReminder(!reminder)}>
                      <AiOutlineCheck />
                    </div>
                    <p>Lembre-me</p>
                  </div>
                  <div className={styles.forgot} onClick={handleForgotPassword}>
                    Esqueci minha senha
                  </div>
                </div>
              </div>
              <div className={styles.buttonContainer}>
                <button type="submit">
                  Entrar
                  <FiChevronRight />
                </button>
              </div>
            </form>
          )}

          {formState === 'Forgot' && (
            <form className={styles.form} onSubmit={handleChangePassword}>
              <span className={styles.formTitle}>Esqueci a Senha</span>
              <div className={styles.formContent}>
                <div className={styles.inputsContainer}>
                  <div className={styles.inputContainer}>
                    <div className={styles.input} role={confirmationCodeOK}>
                      <label htmlFor="code">Código de confirmação</label>
                      <input
                        type="text"
                        name="code"
                        placeholder="Código de confirmação"
                        onChange={(e) => handleInputCode(e)}
                        value={confirmationCode}
                      />
                      <AiOutlineCheck
                        className={styles.check}
                        role={confirmationCodeOK}
                      />
                      <BsExclamation
                        className={styles.danger}
                        role={confirmationCodeOK}
                      />
                    </div>
                    <p className={styles.error}>{confirmationCodeError}</p>
                  </div>
                  <div className={styles.inputContainer}>
                    <div className={styles.input} role={newPasswordOK}>
                      <label htmlFor="newPassword">Nova senha</label>
                      <input
                        type={visible ? 'password' : 'text'}
                        name="newPassword"
                        placeholder="Nova senha"
                        value={newPassword}
                        onChange={handleInputNewPassword}
                      />
                      {visible ? (
                        <FiEyeOff
                          onClick={() => {
                            setVisible(!visible);
                          }}
                          style={{ cursor: 'pointer', marginRight: '8px' }}
                        />
                      ) : (
                        <FiEye
                          onClick={() => {
                            setVisible(!visible);
                          }}
                          style={{ cursor: 'pointer', marginRight: '8px' }}
                        />
                      )}
                      <AiOutlineCheck
                        className={styles.check}
                        role={newPasswordOK}
                      />
                      <BsExclamation
                        className={styles.danger}
                        role={newPasswordOK}
                      />
                    </div>
                    <p className={styles.error}>{newPasswordError}</p>
                  </div>
                  <div className={styles.inputContainer}>
                    <div className={styles.input} role={confirmNewPasswordOK}>
                      <label htmlFor="password">Confirmação Nova Senha</label>
                      <input
                        type={visible ? 'password' : 'text'}
                        name="password"
                        placeholder="Confirmação Nova Senha"
                        value={confirmNewPassword}
                        onChange={handleInputConfirmNewPassword}
                      />
                      {visible ? (
                        <FiEyeOff
                          onClick={() => {
                            setVisible(!visible);
                          }}
                          style={{ cursor: 'pointer', marginRight: '8px' }}
                        />
                      ) : (
                        <FiEye
                          onClick={() => {
                            setVisible(!visible);
                          }}
                          style={{ cursor: 'pointer', marginRight: '8px' }}
                        />
                      )}
                      <AiOutlineCheck
                        className={styles.check}
                        role={confirmNewPasswordOK}
                      />
                      <BsExclamation
                        className={styles.danger}
                        role={confirmNewPasswordOK}
                      />
                    </div>
                    <p className={styles.error}>{confirmNewPasswordError}</p>
                  </div>
                </div>
              </div>
              <div
                className={`${styles.buttonContainer} ${styles.forgotButtonContainer}`}
              >
                <button type="submit">
                  Confirmar
                  <FiChevronRight />
                </button>
              </div>
              <div
                className={styles.link}
                onClick={() => setFormState('SignIn')}
              >
                <button type="button" onClick={() => setFormState('SignIn')}>
                  <FiChevronLeft />
                  Voltar
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
};
